import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { savePendingLog, saveScheduledMap, getScheduledMap } from './alarmLogStore';

const CHANNEL_ID = 'custom_wakeupcall_channel';
const ALARM_ID_BASE = 500_000;
const SLOTS_PER_ALARM = 30;
const SCHEDULE_DAYS_AHEAD = 28;
const ACTION_TYPE_ID = 'ALARM_ACTIONS';

let lastFingerprint = null;
let listenersRegistered = false;

// Callback set by the app to show the AlarmAckDialog
let _onAcknowledged = null;

export function setAcknowledgedCallback(fn) { _onAcknowledged = fn; }

// JS weekday: 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
const DAY_TO_JSDAY = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
};
const WEEKDAY_JSDAYS = [1, 2, 3, 4, 5];
const WEEKEND_JSDAYS = [0, 6];

function getWeekStart() {
  const d = new Date();

  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());

  return d.toISOString().slice(0, 10);
}

function fingerprint(alarms) {
  return alarms
    .map(a => `${a.alarm_id}|${a.alarm_time}|${a.repeat_type}|${a.repeat_days ?? ''}`)
    .sort()
    .join(';') + `@${getWeekStart()}`;
}

async function ensurePermissions() {
  const { display } = await LocalNotifications.requestPermissions();

  return display === 'granted';
}

function parseTime(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);

  return { hour: h, minute: m };
}

function nextOccurrence(hour, minute) {
  const d = new Date();

  d.setHours(hour, minute, 0, 0);
  if (d <= new Date()) d.setDate(d.getDate() + 1);

  return d;
}

// Extract alarm_id from a notification's extra data or from the numeric ID.
// Always returns a number so downstream guards work correctly (including alarm_id=0).
function alarmIdFromNotification(notification) {
  if (notification?.extra?.alarm_id != null) return Number(notification.extra.alarm_id);

  // fallback: reverse the ID math
  return Math.floor((notification.id - ALARM_ID_BASE) / SLOTS_PER_ALARM);
}

// Register the action type (OK button) once per app session
async function ensureActionTypes() {
  try {
    await LocalNotifications.registerActionTypes({
      types: [{
        id: ACTION_TYPE_ID,
        actions: [{ id: 'ok', title: 'OK', foreground: true }],
      }],
    });
  } catch (_) {}
}

// Register received / action listeners once
export function registerAlarmListeners() {
  if (listenersRegistered || !Capacitor.isNativePlatform()) return;
  listenersRegistered = true;

  // Foreground: notification appears while app is open
  LocalNotifications.addListener('localNotificationReceived', (notification) => {
    const alarmId = alarmIdFromNotification(notification);

    if (alarmId == null || alarmId < 0) return;
    // Mark as delivered; user hasn't acted yet
    savePendingLog(alarmId, {
      delivery_status: 'delivered',
      user_action_status: null,
      fired_at: new Date().toISOString(),
      log_date: new Date().toISOString().slice(0, 10),
    });
  });

  // Background/foreground: user taps notification body or action button
  LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
    const alarmId = alarmIdFromNotification(event.notification);

    if (alarmId == null || alarmId < 0) return;

    if (event.actionId === 'ok') {
      savePendingLog(alarmId, {
        delivery_status: 'delivered',
        user_action_status: 'acknowledged',
        fired_at: new Date().toISOString(),
        log_date: new Date().toISOString().slice(0, 10),
      });
      if (_onAcknowledged) _onAcknowledged(event.notification);
    } else {
      // Default tap (notification body) — user opened app but didn't press OK
      savePendingLog(alarmId, {
        delivery_status: 'delivered',
        user_action_status: 'dismissed',
        fired_at: new Date().toISOString(),
        log_date: new Date().toISOString().slice(0, 10),
      });
    }
  });
}

function buildNotifications(alarm) {
  const { alarm_id, title, description, alarm_time, repeat_type, repeat_days } = alarm;

  if (!alarm_time) return [];

  const { hour, minute } = parseTime(alarm_time);
  const body = description || '';
  const now = new Date();

  const base = {
    title,
    body,
    sound: 'wakeupcall.mp3',
    smallIcon: 'ic_notification',
    iconColor: '#2d1b69',
    channelId: CHANNEL_ID,
    actionTypeId: ACTION_TYPE_ID,
    extra: { alarm_id },
  };

  const ID_BASE = ALARM_ID_BASE + alarm_id * SLOTS_PER_ALARM;
  const notifications = [];

  function scheduleForJsDays(jsWeekdays) {
    let slot = 0;

    for (let ahead = 0; ahead < SCHEDULE_DAYS_AHEAD && slot < SLOTS_PER_ALARM; ahead++) {
      const d = new Date();

      d.setDate(d.getDate() + ahead);
      d.setHours(hour, minute, 0, 0);
      if (jsWeekdays.includes(d.getDay()) && d > now) {
        notifications.push({
          ...base,
          id: ID_BASE + slot,
          schedule: { at: new Date(d), allowWhileIdle: true },
        });
        slot++;
      }
    }
  }

  switch (repeat_type) {
    case 'once':
      notifications.push({
        ...base,
        id: ID_BASE,
        schedule: { at: nextOccurrence(hour, minute), allowWhileIdle: true },
      });
      break;

    case 'daily':
      for (let i = 0; i < SCHEDULE_DAYS_AHEAD; i++) {
        const d = new Date();

        d.setDate(d.getDate() + i);
        d.setHours(hour, minute, 0, 0);
        if (d > now) {
          notifications.push({
            ...base,
            id: ID_BASE + i,
            schedule: { at: new Date(d), allowWhileIdle: true },
          });
        }
      }
      break;

    case 'weekdays':
      scheduleForJsDays(WEEKDAY_JSDAYS);
      break;

    case 'weekends':
      scheduleForJsDays(WEEKEND_JSDAYS);
      break;

    case 'custom': {
      if (!repeat_days) break;
      const jsWeekdays = repeat_days
        .split(',')
        .map(d => DAY_TO_JSDAY[d.trim().toLowerCase()])
        .filter(d => d !== undefined);

      scheduleForJsDays(jsWeekdays);
      break;
    }

    default:
      break;
  }

  return notifications;
}

export async function scheduleAlarms(alarms = [], { force = false } = {}) {
  if (!Capacitor.isNativePlatform()) return false;

  const fp = fingerprint(alarms);

  if (!force && fp === lastFingerprint) return true;

  const granted = await ensurePermissions();

  if (!granted) return false;

  await ensureActionTypes();
  registerAlarmListeners();

  const notifications = alarms.flatMap(buildNotifications);

  const idsToCancel = alarms.flatMap(a =>
    Array.from({ length: SLOTS_PER_ALARM }, (_, i) => ({
      id: ALARM_ID_BASE + a.alarm_id * SLOTS_PER_ALARM + i,
    }))
  );

  try { await LocalNotifications.cancel({ notifications: idsToCancel }); } catch (_) {}

  if (notifications.length > 0) {
    await LocalNotifications.schedule({ notifications });

    // Persist scheduled notification → alarm_id + fire_at mapping for fired-alarm detection
    const existing = getScheduledMap();

    for (const n of notifications) {
      existing[String(n.id)] = {
        alarm_id: n.extra.alarm_id,
        fire_at: n.schedule.at.toISOString(),
      };
    }
    saveScheduledMap(existing);
  }

  lastFingerprint = fp;

  return true;
}

export async function getPendingAlarms() {
  if (!Capacitor.isNativePlatform()) {
    return [{ _note: 'Not a native platform — no real pending alarms' }];
  }
  const { notifications } = await LocalNotifications.getPending();

  return notifications.map(n => ({
    id: n.id,
    title: n.title,
    body: n.body,
    sound: n.sound || 'wakeupcall.mp3',
    channelId: n.channelId || CHANNEL_ID,
    _soundFromOs: !!n.sound,
    _channelFromOs: !!n.channelId,
    at: n.schedule?.at ? new Date(n.schedule.at).toLocaleString() : null,
    on: n.schedule?.on ? JSON.stringify(n.schedule.on) : null,
    repeats: n.schedule?.repeats ?? false,
  }));
}

export async function cancelAllAlarms() {
  if (!Capacitor.isNativePlatform()) return 0;
  const { notifications } = await LocalNotifications.getPending();

  if (notifications.length === 0) return 0;
  await LocalNotifications.cancel({ notifications: notifications.map(n => ({ id: n.id })) });
  lastFingerprint = null;

  return notifications.length;
}

export async function cancelAlarmById(alarmId) {
  if (!Capacitor.isNativePlatform()) return;
  const ids = Array.from({ length: SLOTS_PER_ALARM }, (_, i) => ({
    id: ALARM_ID_BASE + alarmId * SLOTS_PER_ALARM + i,
  }));

  try { await LocalNotifications.cancel({ notifications: ids }); } catch (_) {}
}
